import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useActionData, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import Email from '@/assets/svgs/email.svg'
import Phone from '@/assets/svgs/phone.svg'
import { StyledActionButton } from '@/components/Buttons.tsx'
import { Input } from '@/components/Input.tsx'
import {
  FormElement,
  FormElementError,
  FormStyled,
  FormSubmit,
} from '@/components/templates/Form.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { Description, Header1, Text } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress.ts'
import { useAppointmentStore } from '@/store.ts'
import { XpertFormStep } from '@/utils/XpertFormSteps.ts'

import { ActionData } from './actions.ts'

const Detail = styled(Text)`
  color: #222222;
  margin-top: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  font-weight: 500;
`

const ButtonWrapper = styled(FormSubmit)`
  position: absolute;
  bottom: 32px;
  width: calc(100% - 40px);
`

const ConfirmDetails: React.FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'confirmDetails',
  })
  const navigate = useNavigate()
  const actionData = useActionData() as ActionData | undefined
  const { email, phone } = useAppointmentStore(
    (state) => state.patientData || {},
  )
  const setPatientData = useAppointmentStore((state) => state.setPatientData)

  const [edit, setEdit] = useState(!(email && phone))
  const { trackFormError, trackFormInteraction } = useTrackFormProgress(
    XpertFormStep.ConfirmPersonalDetails,
  )

  useEffect(() => {
    const error = actionData?.errors
      ? actionData.errors.map((e) => e.message).join(',')
      : actionData?.error
    if (error) {
      trackFormError({
        error,
      })
    }
  }, [actionData?.errors, actionData?.error])

  const handleEdit = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setEdit(true)
      e.preventDefault()
      trackFormInteraction({
        action: 'click',
        formFieldName: 'edit',
      })
    },
    [],
  )

  return (
    <ScreenWrapper>
      <FormStyled method="post" align="center">
        <Header1>{t('header')}</Header1>
        <Description>{t('description')}</Description>

        {edit ? (
          <>
            <FormElement>
              <Input
                label={t('editDetails.email.label')}
                type="email"
                name="email"
                placeholder={t('editDetails.email.placeholder')}
                value={email || ''}
                onChange={(e) =>
                  setPatientData({
                    email: e.target.value,
                  })
                }
                onFocus={() => {
                  trackFormInteraction({
                    action: 'click',
                    formFieldName: 'email',
                  })
                }}
                errorMessage={
                  actionData?.errors?.find((e) => e.path[0] === 'email')
                    ? actionData.errors.find((e) => e.path[0] === 'email')
                        ?.message
                    : undefined
                }
              />
            </FormElement>

            <FormElement>
              <Input
                label={t('editDetails.telephone.label')}
                type="tel"
                name="telephone"
                placeholder={t('editDetails.telephone.placeholder')}
                value={phone}
                onChange={(e) =>
                  setPatientData({
                    phone: e.target.value,
                  })
                }
                onFocus={() => {
                  trackFormInteraction({
                    action: 'click',
                    formFieldName: 'telephone',
                  })
                }}
                errorMessage={
                  actionData?.errors?.find((e) => e.path[0] === 'phone')
                    ? actionData.errors.find((e) => e.path[0] === 'phone')
                        ?.message
                    : undefined
                }
              />
            </FormElement>

            {actionData?.error && (
              <FormElementError>{actionData.error}</FormElementError>
            )}
          </>
        ) : (
          <>
            <Detail>
              <img src={Email} alt="emailicon" /> {email}
            </Detail>
            <Detail>
              <img src={Phone} alt="phone icon" /> {phone}
            </Detail>
          </>
        )}

        <ButtonWrapper>
          {edit ? (
            <StyledActionButton
              variant="Primary"
              type="submit"
              onClick={() => {
                trackFormInteraction({
                  action: 'next',
                  formFieldName: 'submit',
                })
              }}
            >
              {t('editDetails.actions.saveAndContinue')}
            </StyledActionButton>
          ) : (
            <>
              <StyledActionButton
                variant="Primary"
                onClick={(e) => {
                  navigate('/referral/proposed-appointment')
                  e.preventDefault()
                  trackFormInteraction({
                    action: 'next',
                    formFieldName: 'continue',
                  })
                }}
                id="continue"
                type="button"
              >
                {t('actions.continue')}
              </StyledActionButton>
              <StyledActionButton
                variant="Secondary"
                onClick={handleEdit}
                type="button"
                id="edit"
              >
                {t('actions.edit')}
              </StyledActionButton>
            </>
          )}
        </ButtonWrapper>
      </FormStyled>
    </ScreenWrapper>
  )
}

export { ConfirmDetails }
